import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Check } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ProgressBar from "@/components/ProgressBar";
import EmptyState from "@/components/EmptyState";

export default function Modules() {
    const { user, navVersion, refreshMe } = useAuth();
    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(true);
    // Bump this counter to force /api/modules to refetch — used by the tab-focus
    // listener so any in-between admin actions (group access change, preview-as
    // toggle, etc.) flow through immediately when the user comes back.
    const [refetchTick, setRefetchTick] = useState(0);

    useEffect(() => {
        // Always refresh /me on mount so the user's preview state is current
        // before we ask for modules. This closes the door on any stale state
        // making /api/modules return the wrong group's view.
        let cancelled = false;
        (async () => {
            setLoading(true);
            try { await refreshMe(); } catch (e) { /* ignore */ }
            // Cache-busting param defeats any edge / browser cache on this URL.
            const { data } = await api.get(`/modules?_t=${Date.now()}`);
            if (!cancelled) setModules(data);
        })().finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [user?.id, user?.preview_groups?.length, navVersion, refetchTick]);

    // Refetch whenever the tab regains focus — guards against stale lists when
    // the user has just changed group access in another tab or via admin tools.
    // visibilitychange + focus can BOTH fire on tab-return in some browsers,
    // so a small debounce coalesces them into a single refetch.
    useEffect(() => {
        let timer = null;
        const schedule = () => {
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => setRefetchTick((t) => t + 1), 200);
        };
        const onVisible = () => {
            if (document.visibilityState === "visible") schedule();
        };
        document.addEventListener("visibilitychange", onVisible);
        window.addEventListener("focus", schedule);
        return () => {
            document.removeEventListener("visibilitychange", onVisible);
            window.removeEventListener("focus", schedule);
            if (timer) clearTimeout(timer);
        };
    }, []);

    // Effective group context (preview wins for admins).
    const effectiveGroups = useMemo(() => {
        const preview = user?.preview_groups || [];
        if (preview.length > 0) return preview;
        return user?.groups || [];
    }, [user?.groups, user?.preview_groups]);

    const groupLabel = effectiveGroups.length === 0
        ? null
        : effectiveGroups.map((g) => g.title).join(" + ");

    const isStaff = user?.role === "admin" || user?.role === "trainer";
    const isPreviewing = (user?.preview_groups || []).length > 0;

    const kicker = groupLabel
        ? `${groupLabel} · Curriculum`
        : (isStaff ? "All Curricula" : "Curriculum");

    const subtitle = (() => {
        if (groupLabel) {
            return `The modules available to ${groupLabel}. Take them end to end.`;
        }
        if (isStaff && !isPreviewing) {
            return "Every module in the system. Use Preview As on a group to see what they see.";
        }
        return "A focused sequence designed to be taken end to end.";
    })();

    return (
        <div className="space-y-10" data-testid="modules-page">
            <div className="flex items-end justify-between gap-6">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono mb-3" data-testid="modules-kicker">
                        {kicker}
                    </div>
                    <h1 className="font-editorial text-4xl md:text-5xl text-ink-900 leading-[1.05]">Modules.</h1>
                    <p className="mt-3 text-base text-ink-500 max-w-xl" data-testid="modules-subtitle">
                        {subtitle}
                    </p>
                </div>
                <div className="text-xs text-ink-500 font-mono uppercase tracking-[0.15em] hidden md:block" data-testid="modules-count">
                    {loading ? "—" : `${modules.length} ${modules.length === 1 ? "module" : "modules"}`}
                </div>
            </div>

            {loading ? (
                <div className="text-sm text-ink-500" data-testid="modules-loading">Loading…</div>
            ) : modules.length === 0 ? (
                <EmptyState
                    title={groupLabel ? `No modules for ${groupLabel} yet` : "No modules published yet"}
                    description={groupLabel
                        ? "An administrator hasn't given this group access to any modules. Open Admin → Groups & Access to assign content."
                        : "Your administrator will publish modules from the Admin panel. They'll appear here once available."}
                    icon={BookOpen}
                    testId="modules-empty"
                />
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-ink-300 border border-ink-300">
                    {modules.map((m, idx) => (
                        <Link
                            key={m.id}
                            to={`/modules/${m.id}`}
                            data-testid={`module-card-${m.id}`}
                            className="group bg-white p-8 hover:bg-ink-100/60 transition-colors flex flex-col"
                        >
                            <div className="flex items-start justify-between mb-6">
                                <div className="font-mono text-xs uppercase tracking-[0.2em] text-ink-400">
                                    {String(idx + 1).padStart(2, "0")}
                                </div>
                                {(m.completed ?? ((m.progress_pct ?? 0) === 100)) && (
                                    <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-mono text-haevn-700 bg-haevn-50 border border-haevn-100 px-2 py-1">
                                        <Check size={10} strokeWidth={2} /> Complete
                                    </div>
                                )}
                            </div>

                            <h3 className="font-heading text-xl text-ink-900 group-hover:text-haevn-700 transition-colors">{m.title}</h3>
                            {m.description && <p className="text-sm text-ink-500 mt-2 line-clamp-3">{m.description}</p>}

                            <div className="mt-auto pt-6">
                                <ProgressBar value={m.progress_pct ?? 0} size="sm" />
                                <div className="mt-2 flex items-center justify-between text-[11px] font-mono uppercase tracking-[0.15em] text-ink-400">
                                    <span>{m.completed_lessons ?? 0}/{m.lesson_count ?? 0} lessons</span>
                                    <span>{m.estimated_minutes ?? 30} min</span>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}
